import { Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import {
    IMentionMeCommonParams,
    IMentionMeConversionmessageParams,
    IMentionMeDashboardParams,
    IMentionMeOrderParams,
    IMentionMeRefereefindParams,
    IMentionMeReferrerofferParams,
    MentionMePlace
} from '@df/module-mention-me/mention-me.interfaces';

declare global {
    interface Window {
        MentionMeFiredTags?: boolean;
    }
}

@Injectable({
    providedIn: 'root'
})
export class MentionMeService {
    constructor(
        protected app: App,
        private request: RequestService
    ) {}

    getQuery(
        place: MentionMePlace,
        mmid: string,
        implementation?: string,
        situation?: string,
        checkoutSuccessData?: Checkout.ISuccessData
    ) {
        const params = this.getParams(place, implementation, situation, checkoutSuccessData);

        const apiBaseUrl =
            this.getApiBaseUrl(place, mmid) +
            (place === MentionMePlace.dashboard ? '/' + this.app.customer.model.data?.email || 'email address' : '');

        return apiBaseUrl + (params ? '?' + this.request.serialize(params as any) : '');
    }

    protected getApiBaseUrl(place: MentionMePlace, mmid: string) {
        const domain = this.app.dfConfig.environment === 'production' ? 'tag.mention-me.com' : 'tag-demo.mention-me.com';
        return '//' + domain + '/api/v2/' + place + '/' + mmid;
    }

    private getParams(
        place: MentionMePlace,
        implementation?: string,
        situation?: string,
        checkoutSuccessData?: Checkout.ISuccessData
    ): Partial<IMentionMeCommonParams> {
        switch (place) {
            case MentionMePlace.dashboard:
                return this.getDashboardParams(situation, implementation);
            case MentionMePlace.refereefind:
                return this.getRefereefindParams(situation, implementation);
            case MentionMePlace.referreroffer:
                return this.getReferrerofferParams(situation, checkoutSuccessData, implementation);
            case MentionMePlace.conversionmessage:
                return this.getConversionmessageParams(situation, implementation);
            case MentionMePlace.order:
                return this.getOrderParams(checkoutSuccessData, implementation);
        }
    }

    /**
     * @url https://demo.mention-me.com/api-demo/v2/biscuiteers/instructions/dashboard
     */

    private getDashboardParams(situation = 'dashboard', implementation?: string): IMentionMeDashboardParams {
        const customerData = this.app.customer.model.data;
        return {
            ...this.getCommonParams(implementation),
            situation: situation || 'dashboard',
            firstname: customerData.firstname || '',
            surname: customerData.lastname || '',
            customer_id: customerData.id,
            email: customerData.email || ''
        };
    }

    /**
     * @url https://demo.mention-me.com/api-demo/v2/biscuiteers/instructions/referee
     */
    private getRefereefindParams(situation = 'checkout', implementation?: string): IMentionMeRefereefindParams {
        return { ...this.getCommonParams(implementation), situation };
    }

    /**
     * @url https://demo.mention-me.com/api-demo/v2/biscuiteers/instructions/referrer
     */
    protected getReferrerofferParams(
        situation = 'postpurchase',
        data?: Checkout.ISuccessData,
        implementation?: string
    ): Partial<IMentionMeReferrerofferParams> {
        return {
            ...this.getOrderParams(data, implementation),
            situation: situation,
            firstname: data?.customer?.firstname || '',
            surname: data?.customer.lastname || ''
        };
    }

    /**
     * @url https://demo.mention-me.com/api-demo/v2/biscuiteers/instructions/conversionmessage
     */
    private getConversionmessageParams(situation = 'homepage', implementation?: string): IMentionMeConversionmessageParams {
        return {
            ...this.getCommonParams(implementation),
            situation
        };
    }

    /**
     * @url https://demo.mention-me.com/api-demo/v2/biscuiteers/instructions/order_tracking
     */
    private getOrderParams(data?: Checkout.ISuccessData, implementation?: string): Partial<IMentionMeOrderParams> {
        return {
            ...this.getCommonParams(implementation),
            customer_id: data?.customer.id,
            email: data?.customer.email,
            order_number: data?.number,
            segment: data?.customer.can_register ? 'new' : 'existing',
            order_total: data?.total,
            order_currency: data?.currency,
            order_date: data?.date
        };
    }

    private getCommonParams(implementation?: string): IMentionMeCommonParams {
        const params: IMentionMeCommonParams = {
            locale: this.app.data.locale || 'en_gb'
        };

        if (implementation) {
            params.implementation = implementation;
        }

        return params;
    }
}
